import React from "react";

import { Grid, Card, Box, Typography, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { useGetWeddingInvitationStatsQuery } from "../../../../store/services/invitations/invitations.service";
import { useParams, useSearchParams } from "react-router-dom";
import { StatWrapper, GridContainer, LockedOverviewWrapper } from "./styles";
import { Visibility, CheckCircle, Cancel, Rsvp, CameraAltSharp, Lock } from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { BarChart } from "@mui/x-charts/BarChart";
import { red, green, purple, orange, blue, grey } from "@mui/material/colors";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { getFullInvitationsState, setOpenPricingModal } from "../../../../store/slices/invitations/invitations.slice";
import { INVITATION_PRICING_PLANS_ENUM } from "../../../../store/services/invitations/dto/invitations.dto";
import { UpgradeBtn, WhiteCrown } from "../../styles";
import { trackEvent } from "../../../../utils/analytics";
import { trackMetaEvent } from "../../../../utils/metaPixel";
import { trackTikTokEvent } from "../../../../utils/tiktokPixel";

const STAT_COLORS = {
	views: orange[500],
	responses: purple[500],
	accepted: green[600],
	rejected: red[400],
	memories: blue[500]
};

const LegendDot = ({ color, label, value }: { color: string; label: string; value: number }) => (
	<Box display="flex" alignItems="center" gap={0.75}>
		<Box sx={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
		<Typography variant="caption" color="textSecondary">
			{label} ({value.toLocaleString()})
		</Typography>
	</Box>
);

const Overview = () => {
	const params = useParams();
	const id = params.id;
	const [searchParams] = useSearchParams();
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const password = searchParams.get("password");
	const dispatch = useAppDispatch();
	const { invitationPayedData } = useAppSelector(getFullInvitationsState);
	const { data: stats, isLoading } = useGetWeddingInvitationStatsQuery({
		id: id as string,
		password: password as string
	});
	const isLocked = invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE;

	const views = stats?.total_views || 0;
	const responses = stats?.total_responses || 0;
	const accepted = stats?.accepted_responses || 0;
	const rejected = stats?.rejected_responses || 0;
	const memories = stats?.total_memories || 0;
	const pending = Math.max(0, responses - accepted - rejected);

	const responseRate = views > 0 ? Math.round((responses / views) * 100) : 0;
	const acceptanceRate = responses > 0 ? Math.round((accepted / responses) * 100) : 0;

	const pieWidth = isXsScreen ? 220 : isSmallerScreen ? 280 : 260;

	if (isLoading) {
		return (
			<GridContainer container spacing={3}>
				<Grid size={12}>
					<Box display="flex" flexWrap="wrap" gap={2}>
						{[1, 2, 3, 4, 5].map(i => (
							<Skeleton key={i} variant="rounded" sx={{ flex: 1, minWidth: 185, height: 82 }} />
						))}
					</Box>
				</Grid>
				<Grid size={isSmallerScreen ? 12 : 8}>
					<Skeleton variant="rounded" height={340} />
				</Grid>
				<Grid size={isSmallerScreen ? 12 : 4}>
					<Skeleton variant="rounded" height={340} />
				</Grid>
				<Grid size={isSmallerScreen ? 12 : 6}>
					<Skeleton variant="rounded" height={320} />
				</Grid>
				<Grid size={isSmallerScreen ? 12 : 6}>
					<Skeleton variant="rounded" height={320} />
				</Grid>
			</GridContainer>
		);
	}

	if (isLocked) {
		return (
			<LockedOverviewWrapper>
				<Box className="icon_ring">
					<Visibility />
				</Box>
				<Typography className="views_value">{views.toLocaleString()}</Typography>
				<Typography className="views_label">Total Views</Typography>
				<Typography className="teaser_text">
					Upgrade to Signature to unlock RSVP breakdown, response &amp; acceptance rates, guest journey analytics, and
					memories tracking.
				</Typography>
				<Box className="locked_row">
					{[Rsvp, CheckCircle, Cancel, CameraAltSharp].map((Icon, i) => (
						<Box key={i} className="locked_stat">
							<Icon />
							<Lock className="lock_badge" />
						</Box>
					))}
				</Box>
				<UpgradeBtn
					variant={"contained"}
					startIcon={<WhiteCrown />}
					onClick={() => {
						trackEvent("upgrade_btn", {
							scope: "Invitations",
							description: "Upgrade Button"
						});
						trackMetaEvent("upgrade_btn", { invitation_id: id }, true);
						trackTikTokEvent("upgrade_btn", { invitation_id: id });
						dispatch(setOpenPricingModal(true));
					}}
				>
					Upgrade to Signature
				</UpgradeBtn>
			</LockedOverviewWrapper>
		);
	}

	const statCards = [
		{
			label: "Total Views",
			value: views,
			icon: <Visibility />,
			color: STAT_COLORS.views,
			sub: "Invitation page visits"
		},
		{
			label: "Total Responses",
			value: responses,
			icon: <Rsvp />,
			color: STAT_COLORS.responses,
			sub: `${responseRate}% response rate`
		},
		{
			label: "Accepted",
			value: accepted,
			icon: <CheckCircle />,
			color: STAT_COLORS.accepted,
			sub: responses > 0 ? `${acceptanceRate}% of responses` : "No responses yet"
		},
		{
			label: "Rejected",
			value: rejected,
			icon: <Cancel />,
			color: STAT_COLORS.rejected,
			sub: responses > 0 ? `${100 - acceptanceRate}% of responses` : "No responses yet"
		},
		{
			label: "Memories",
			value: memories,
			icon: <CameraAltSharp />,
			color: STAT_COLORS.memories,
			sub: "Photos & videos shared"
		}
	];

	return (
		<GridContainer container spacing={3}>
			{/* ── Stat Cards ── */}
			<Grid size={12}>
				<Box display="flex" flexWrap="wrap" gap={2}>
					{statCards.map((card, i) => (
						<StatWrapper key={i} $statColor={card.color}>
							<Box className="icon">{card.icon}</Box>
							<Box className="stats">
								<Typography fontWeight={800} variant="h5" lineHeight={1.1}>
									{card.value.toLocaleString()}
								</Typography>
								<Typography fontWeight={600} fontSize={13} mt={0.3}>
									{card.label}
								</Typography>
								<Typography color="textSecondary" fontSize={11} mt={0.25}>
									{card.sub}
								</Typography>
							</Box>
						</StatWrapper>
					))}
				</Box>
			</Grid>

			{/* ── Guest Journey Bar Chart ── */}
			<Grid size={12}>
				<Card sx={{ p: 3, height: "100%", overflow: "hidden" }}>
					<Typography fontWeight={700} fontSize={16}>
						Guests Journey
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block" mb={2}>
						Funnel from invitation views to confirmed responses
					</Typography>
					<BarChart
						xAxis={[
							{
								data: ["Views", "Responses", "Accepted", "Rejected", "Memories"],
								scaleType: "band",
								colorMap: {
									type: "ordinal",
									colors: [orange[400], purple[400], green[400], red[400], blue[400]]
								}
							}
						]}
						yAxis={[{ label: "Count" }]}
						series={[{ data: [views, responses, accepted, rejected, memories] }]}
						height={260}
						borderRadius={6}
						margin={{ left: 50, right: 16, top: 10, bottom: 40 }}
					/>
				</Card>
			</Grid>

			{/* ── RSVP Breakdown Donut ── */}
			<Grid size={isSmallerScreen ? 12 : 6}>
				<Card sx={{ p: 3, minHeight: "330px", overflow: "hidden" }}>
					<Typography fontWeight={700} fontSize={16}>
						RSVP Breakdown
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block" mb={2}>
						{responses.toLocaleString()} total responses received
					</Typography>
					<Box display="flex" justifyContent="center" sx={{ overflow: "hidden" }}>
						<PieChart
							series={[
								{
									innerRadius: 55,
									outerRadius: 100,
									paddingAngle: 3,
									cornerRadius: 4,
									data: [
										{ label: "Accepted", value: accepted, color: green[400] },
										{ label: "Rejected", value: rejected, color: red[400] },
										...(pending > 0 ? [{ label: "Pending", value: pending, color: orange[300] }] : [])
									],
									arcLabel: item => {
										const pct = Math.round((item.value / (responses || 1)) * 100);
										return pct >= 8 ? `${pct}%` : "";
									},
									arcLabelMinAngle: 20,
									highlightScope: { fade: "global", highlight: "item" }
								}
							]}
							width={pieWidth}
							height={220}
							margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
							hideLegend
						/>
					</Box>
					<Box display="flex" gap={2} flexWrap="wrap" justifyContent="center" mt={1.5}>
						<LegendDot color={green[400]} label="Accepted" value={accepted} />
						<LegendDot color={red[400]} label="Rejected" value={rejected} />
						{pending > 0 && <LegendDot color={orange[300]} label="Pending" value={pending} />}
					</Box>
				</Card>
			</Grid>

			{/* ── Response Rate Gauge ── */}
			<Grid size={isSmallerScreen ? 12 : 3}>
				<Card sx={{ p: 3, minHeight: "330px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
					<Typography fontWeight={700} fontSize={16}>
						Response Rate
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block" mb={1}>
						Share of viewers who submitted an RSVP
					</Typography>
					<Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
						<Gauge
							value={responseRate}
							startAngle={-110}
							endAngle={110}
							height={190}
							innerRadius="68%"
							outerRadius="100%"
							text={() => `${responseRate}%`}
							sx={{
								[`& .${gaugeClasses.valueText}`]: { fontSize: 30, fontWeight: 700, fill: purple[600] },
								[`& .${gaugeClasses.valueArc}`]: { fill: purple[400] },
								[`& .${gaugeClasses.referenceArc}`]: { fill: grey[100] }
							}}
						/>
						<Typography variant="caption" color="textSecondary" textAlign="center" mt={-1}>
							{responses.toLocaleString()} of {views.toLocaleString()} viewers responded
						</Typography>
					</Box>
				</Card>
			</Grid>

			{/* ── Acceptance Rate Gauge ── */}
			<Grid size={isSmallerScreen ? 12 : 3}>
				<Card sx={{ p: 3, minHeight: "330px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
					<Typography fontWeight={700} fontSize={16}>
						Acceptance Rate
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block" mb={1}>
						Share of respondents who confirmed attendance
					</Typography>
					<Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
						<Gauge
							value={acceptanceRate}
							startAngle={-110}
							endAngle={110}
							height={190}
							innerRadius="68%"
							outerRadius="100%"
							text={() => `${acceptanceRate}%`}
							sx={{
								[`& .${gaugeClasses.valueText}`]: { fontSize: 30, fontWeight: 700, fill: green[700] },
								[`& .${gaugeClasses.valueArc}`]: { fill: green[400] },
								[`& .${gaugeClasses.referenceArc}`]: { fill: grey[100] }
							}}
						/>
						<Typography variant="caption" color="textSecondary" textAlign="center" mt={-1}>
							{accepted.toLocaleString()} accepted of {responses.toLocaleString()} responses
						</Typography>
					</Box>
				</Card>
			</Grid>
		</GridContainer>
	);
};

export default Overview;
